"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PortalModal } from "@/components/ui/portal-modal";
import { ROUTES } from "@/constants/routes";
import { useAutomaticStreakCgu } from "@/hooks/automatic-streak/useAutomaticStreakCgu";
import { cn } from "@/lib/utils";
import { AlertTriangle, Bot, FileText } from "lucide-react";
import Link from "next/link";

interface AutomaticStreakCguModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

interface CguCheckboxItemProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const CguCheckboxItem = ({ id, checked, onCheckedChange, icon, title, description }: CguCheckboxItemProps) => {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200",
        "hover:border-cede-primary/30 hover:bg-cede-primary/5",
        checked ? "border-cede-primary bg-cede-primary/10" : "border-cede-border-container bg-cede-bg-component",
      )}
    >
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} className="mt-1" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="font-medium text-cede-text-primary">{title}</span>
        </div>
        <p className="text-sm text-cede-text-secondary leading-relaxed">{description}</p>
      </div>
    </label>
  );
};

export const AutomaticStreakCguModal = ({ open, onClose, onConfirm }: AutomaticStreakCguModalProps) => {
  const {
    riskAcknowledged,
    automatedTradingAcknowledged,
    termsAndConditionsAccepted,
    canProceed,
    isSubmitting,
    setRiskAcknowledged,
    setAutomatedTradingAcknowledged,
    setTermsAndConditionsAccepted,
    handleConfirm,
    reset,
  } = useAutomaticStreakCgu();

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const success = await handleConfirm();
    if (success) {
      // Call onConfirm which will handle the next step (opening config modal)
      // Don't call onClose() here - onConfirm handles the transition
      onConfirm?.();
    }
  };

  return (
    <PortalModal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
      showCloseButton
      className="sm:max-w-[560px]"
    >
      <PortalModal.Header>
        <PortalModal.Title>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cede-warning/20">
              <AlertTriangle className="w-6 h-6 text-cede-warning" />
            </div>
            <div>
              <span className="block">Autopilot Terms</span>
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-cede-warning/20 text-cede-warning">
                Beta Feature
              </span>
            </div>
          </div>
        </PortalModal.Title>
        <PortalModal.Description className="mt-2">
          Before using the Autopilot feature, please read and acknowledge the following terms.
        </PortalModal.Description>
      </PortalModal.Header>

      <PortalModal.Content className="flex flex-col gap-3">
        <CguCheckboxItem
          id="risk-acknowledgment"
          checked={riskAcknowledged}
          onCheckedChange={setRiskAcknowledged}
          icon={<AlertTriangle className="w-4 h-4 text-cede-warning" />}
          title="Risk Acknowledgment"
          description="I understand this is a beta feature that may contain bugs. Trading involves financial risk and may result in capital loss depending on market conditions."
        />

        <CguCheckboxItem
          id="automated-trading"
          checked={automatedTradingAcknowledged}
          onCheckedChange={setAutomatedTradingAcknowledged}
          icon={<Bot className="w-4 h-4 text-cede-primary" />}
          title="Automated Trading Authorization"
          description="I authorize Cede Labs to execute automated buy and sell trades on my behalf. Trades will automatically stop when the epoch ends, maximum volume is reached, or a trade fails."
        />

        {/* Terms and Conditions with link */}
        <label
          htmlFor="terms-and-conditions"
          className={cn(
            "flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200",
            "hover:border-cede-primary/30 hover:bg-cede-primary/5",
            termsAndConditionsAccepted
              ? "border-cede-primary bg-cede-primary/10"
              : "border-cede-border-container bg-cede-bg-component",
          )}
        >
          <Checkbox
            id="terms-and-conditions"
            checked={termsAndConditionsAccepted}
            onCheckedChange={setTermsAndConditionsAccepted}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-cede-text-secondary" />
              <span className="font-medium text-cede-text-primary">Terms and Conditions</span>
            </div>
            <p className="text-sm text-cede-text-secondary leading-relaxed">
              I have read and accept the{" "}
              <Link
                href={ROUTES.TERMS_AND_CONDITIONS}
                target="_blank"
                className="text-cede-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms and Conditions
              </Link>
              , including the Automated Trading section.
            </p>
          </div>
        </label>
      </PortalModal.Content>

      <PortalModal.Footer className="flex-col sm:flex-row gap-3">
        <Button variant="secondary" size="medium" onClick={handleClose} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={!canProceed || isSubmitting}
          className="w-full sm:w-auto"
        >
          {isSubmitting ? "Confirming..." : "Accept & Continue"}
        </Button>
      </PortalModal.Footer>
    </PortalModal>
  );
};
